/* Bulgarian interface strings for content pages that were English-only on
 * /bg/ (visual audit, 21 Oct 2026). Same mechanism as the tool catalogues:
 * components keep their English literals wrapped in tr(); a missing entry
 * falls back to English and is reported by src/__tests__/tools-i18n.test.mjs.
 * REVIEW-BG draft. Legal documents, article bodies and customer quotes are
 * intentionally NOT translated here. */
import type { Catalogue } from "./index";
import {
  DWDM_CAPACITY_RANGE_LABEL,
  IP_TRANSIT_MIN_LABEL,
  IP_TRANSIT_MAX_LABEL,
} from "@/lib/pricing";
import {
  IP_TRANSIT_SLA_PERCENT,
  BGP_PREFIX_COUNT_LABEL,
  HERO_SOFIA_ISTANBUL_LATENCY_MS,
  formatLatencyApproxNoSpace,
  CORRIDOR_LATENCY_LABELS,
  CORRIDOR_LATENCY_LABELS_BG,
} from "@/lib/network-metrics";

export const SERVICES_BG: Catalogue = {
  "Connectivity Products": "Услуги за свързаност",
  "Built for Scale": "Създадени за растеж",
  "From raw fiber to fully managed cloud on-ramps — everything your network needs, available on-demand across Southeast Europe.":
    "От тъмно влакно до напълно управлявани връзки към облаци — всичко, от което мрежата ви има нужда, при поискване в Югоизточна Европа.",
  "IP Transit": "IP Transit",
  "Ethernet / L2VPN": "Ethernet / L2VPN",
  "Wavelengths (DWDM)": "DWDM вълни",
  "Dark Fiber": "Тъмно влакно",
  [`Premium global routing via Tier-1 peering. Full-table BGP with ${BGP_PREFIX_COUNT_LABEL} prefixes, ${IP_TRANSIT_MIN_LABEL} to ${IP_TRANSIT_MAX_LABEL} capacity, and ${IP_TRANSIT_SLA_PERCENT} SLA.`]:
    `Глобална маршрутизация чрез пиъринг с Tier-1 оператори. Пълна BGP таблица с ${BGP_PREFIX_COUNT_LABEL} префикса, капацитет от ${IP_TRANSIT_MIN_LABEL} до ${IP_TRANSIT_MAX_LABEL} и SLA ${IP_TRANSIT_SLA_PERCENT}.`,
  [`${BGP_PREFIX_COUNT_LABEL} prefixes`]: `${BGP_PREFIX_COUNT_LABEL} префикса`,
  [`${formatLatencyApproxNoSpace(HERO_SOFIA_ISTANBUL_LATENCY_MS)} to Istanbul`]: `${formatLatencyApproxNoSpace(HERO_SOFIA_ISTANBUL_LATENCY_MS)} до Истанбул`,
  "Transparent Layer 2 connectivity over MPLS backbone. Point-to-point and multipoint with full traffic isolation.":
    "Прозрачна свързаност на ниво 2 по MPLS магистрала. Точка до точка и много точки, с пълно разделяне на трафика.",
  [`Dedicated optical capacity with coherent optics. Deterministic latency, protocol transparent, ${DWDM_CAPACITY_RANGE_LABEL} per wavelength.`]:
    `Отделен оптичен капацитет с кохерентна оптика. Предвидимо закъснение, независимо от протокола, ${DWDM_CAPACITY_RANGE_LABEL} на вълна.`,
  [`${DWDM_CAPACITY_RANGE_LABEL} per λ`]: `${DWDM_CAPACITY_RANGE_LABEL} на λ`,
  "Coherent optics": "Кохерентна оптика",
  "Unlit G.652D fibre in the Sofia metro today; national and cross-border corridors on the roadmap. Your equipment, your protocols.":
    "Тъмно влакно G.652D, засега в рамките на София. Национални и трансгранични трасета са в плановете. Вашето оборудване, вашите протоколи.",
  "Sofia metro today": "Засега в София",
  "Kalotina mid-2027": "Калотина, средата на 2027",
  "Route A Q1 2028": "Трасе A, I трим. 2028",
  "IRU / Lease": "IRU или наем",
  "Not sure which service fits?": "Не сте сигурни коя услуга ви трябва?",
  "Use our NaaS Configurator to design your optimal connectivity solution, or contact our engineers for a custom consultation.":
    "Използвайте NaaS конфигуратора, за да съберете подходящото решение, или се свържете с инженерите ни за консултация.",
  "Open NaaS Configurator": "Отвори NaaS конфигуратора",
  "Talk to an Engineer": "Говорете с инженер",
};

export const FINDER_BG: Catalogue = {
  "Solution Finder": "Подбор на услуга",
  "Find the right service": "Намерете подходящата услуга",
  "Answer four quick questions and we'll match you to the Sofia Connect service that fits your requirements — with indicative pricing.":
    "Отговорете на четири кратки въпроса и ще ви предложим услугата на Sofia Connect, която отговаря на нуждите ви, с ориентировъчна цена.",
  STEP: "СТЪПКА",
  "Traffic Type": "Вид трафик",
  Geography: "География",
  Capacity: "Капацитет",
  Recommendation: "Препоръка",
  "What do you need?": "Какво ви трябва?",
  "Select the type of connectivity that best describes your requirement.":
    "Изберете вида свързаност, който най-добре описва нуждата ви.",
  "Internet Access": "Достъп до интернет",
  "Direct IP transit for your network": "Директен IP транзит за вашата мрежа",
  "Private Connectivity": "Частна свързаност",
  "Point-to-point or multipoint between locations": "Точка до точка или между много точки",
  "High-Capacity Transport": "Пренос с голям капацитет",
  "Wavelength services for maximum bandwidth": "Оптични вълни за максимален капацитет",
  "Raw Infrastructure": "Физическа инфраструктура",
  "Unlit fibre you control (Sofia metro today)": "Тъмно влакно под ваш контрол (засега в София)",
  "IP Transit": "IP Transit",
  "Ethernet L2VPN": "Ethernet L2VPN",
  "DWDM Wavelengths": "DWDM вълни",
  "Dark Fiber": "Тъмно влакно",
  "Where do you need it?": "Къде ви трябва?",
  "Select one or more PoP locations. We'll use these to tailor your price estimate.":
    "Изберете една или повече локации. По тях ще изчислим ориентировъчната цена.",
  "Selected:": "Избрани:",
  "How much capacity?": "Какъв капацитет?",
  "Choose your committed bandwidth tier. You can always upgrade via the NaaS platform.":
    "Изберете гарантирания капацитет. Винаги може да го увеличите през NaaS платформата.",
  "Starter / burst": "Начален / с пикове",
  "Standard enterprise": "Стандартен за бизнес",
  "High-performance": "Висока производителност",
  "Carrier-grade": "За оператори",
  "Contact for quote": "Поискайте оферта",
  Contact: "Запитване",
  "From €1,100": "От €1 100",
  "/month est.": "/мес. ориент.",
  "Prices are indicative list rates in EUR/month. Final pricing depends on route, contract term, and volume commitments.":
    "Цените са ориентировъчни, в евро на месец. Окончателната цена зависи от трасето, срока на договора и обема.",
  "Your Recommendation": "Нашата препоръка",
  "BGP full-table internet connectivity": "Интернет свързаност с пълна BGP таблица",
  "Full-table BGP IP transit with upstream diversity, regional IXP peering, and DDoS mitigation. Ideal for ISPs, cloud platforms, content providers, and enterprise networks requiring high-quality internet connectivity.":
    "IP транзит с пълна BGP таблица, няколко независими доставчика, регионален пиъринг и защита от DDoS. Подходящ за интернет доставчици, облачни платформи, доставчици на съдържание и корпоративни мрежи.",
  "Transparent Layer 2 private connectivity": "Прозрачна частна свързаност на ниво 2",
  "Point-to-point and multipoint Ethernet services across Sofia Connect PoPs. Carrier-grade MPLS backbone with sub-millisecond failover. Ideal for data center interconnect, enterprise WAN, and financial networks.":
    "Ethernet услуги точка до точка и между много точки през PoP-овете на Sofia Connect. MPLS магистрала с превключване под милисекунда. Подходящи за връзка между центрове за данни, корпоративни WAN и финансови мрежи.",
  "Dedicated optical wavelength transport": "Пренос по отделна оптична вълна",
  "Unlit fibre — full physical control": "Тъмно влакно — пълен физически контрол",
  "Unlit G.652D fibre in the Sofia metro today: reaches every data centre and business building in the city. Run your own DWDM, custom protocols or experimental equipment on a monthly lease or 15-year IRU. National and cross-border corridors are on the roadmap (Kalotina mid-2027; Route A Q1 2028) — enquire for timing.":
    "Тъмно влакно G.652D, засега в рамките на София: стига до всеки център за данни и бизнес сграда в града. Пуснете собствен DWDM, свои протоколи или експериментално оборудване, с месечен наем или IRU за 15 години. Национални и трансгранични трасета са в плановете (Калотина в средата на 2027, трасе A през I трим. 2028) — попитайте ни за срокове.",
  "Physical layer only": "Само физически слой",
  [CORRIDOR_LATENCY_LABELS.sofiaFrankfurt]: CORRIDOR_LATENCY_LABELS_BG.sofiaFrankfurt,
  [`Coherent DWDM wavelengths from ${DWDM_CAPACITY_RANGE_LABEL} across the Sofia Connect network. Protocol-transparent, speed-of-light latency, zero congestion. Ideal for AI/ML data flows, broadcast, and ultra-low-latency trading.`]:
    `Кохерентни DWDM вълни от ${DWDM_CAPACITY_RANGE_LABEL} в мрежата на Sofia Connect. Независими от протокола, със закъснение, ограничено само от физиката, и без претоварване. Подходящи за AI/ML потоци, телевизионно разпръскване и търговия с ниско закъснение.`,
  "Sofia metro today": "Засега в София",
  "< 8 ms regional": "< 8 ms в региона",
  "100G / 400G-ready": "Готово за 100G / 400G",
  "Uptime SLA": "SLA за наличност",
  Latency: "Закъснение",
  Locations: "Локации",
  "All PoPs": "Всички PoP-ове",
  "Estimated Monthly Cost": "Ориентировъчна месечна цена",
  "Primary PoP": "Основен PoP",
  "indicative list rate": "ориентировъчна цена",
  "Schedule a discovery call with Sofia Connect sales": "Запазете разговор с екипа по продажби на Sofia Connect",
  "Schedule a Discovery Call": "Запазете разговор",
  "Configure in NaaS Platform": "Конфигурирай в NaaS платформата",
  "Request a Quote": "Поискайте оферта",
  "View full details": "Всички подробности",
  "Start over": "Започни отначало",
  Back: "Назад",
  Continue: "Напред",
  "See recommendation": "Виж препоръката",
};

export const CASE_STUDIES_BG: Catalogue = {
  "400G DWDM from Armenia to Frankfurt": "400G DWDM от Армения до Франкфурт",
  "A 400G coherent wavelength in production from Agarak on the Armenian border through to Frankfurt — routed over the Caucasus Cable System submarine link to keep traffic clear of Türkiye terrestrial transit.":
    "Кохерентна вълна 400G в реална експлоатация от Агарак на арменската граница до Франкфурт. Минава през подводната връзка Caucasus Cable System, за да заобиколи наземния транзит през Турция.",
  "Wholesale · 400G DWDM · Submarine · Bypass": "Едро · 400G DWDM · Подводен кабел · Заобикаляне",
  Capacity: "Капацитет",
  Route: "Трасе",
  Submarine: "Подводен кабел",
  "Expanding East–West Connectivity with Zayo Europe": "Повече свързаност между Изтока и Запада със Zayo Europe",
  "How Sofia Connect partnered with Zayo Europe to scale capacity, deliver low-latency routes into Western Europe's digital hubs, and reinforce a high-availability Tier 1 backbone for Eastern European customers.":
    "Как Sofia Connect и Zayo Europe увеличиха капацитета, изградиха трасета с ниско закъснение към големите центрове в Западна Европа и укрепиха надеждна Tier 1 магистрала за клиенти от Източна Европа.",
  "Wholesale · DWDM · IP Backbone": "Едро · DWDM · IP магистрала",
  Backbone: "Магистрала",
  Redundancy: "Резервираност",
  "Multi-link": "Няколко връзки",
  Reach: "Обхват",
  "East ↔ West EU": "Изток ↔ Запад в ЕС",
  "Case Studies": "Казуси",
  "Partnership Stories": "Истории на партньорства",
  "How Sofia Connect works alongside global carriers and customers to deliver resilient, high-capacity connectivity across Southeast Europe and beyond.":
    "Как Sofia Connect работи с глобални оператори и клиенти, за да осигури надеждна свързаност с голям капацитет в Югоизточна Европа и отвъд нея.",
  "Read the story →": "Прочетете историята →",
  "Build your own success story": "Нека изградим и вашата история",
  "Talk to our team about capacity, routing, and wholesale partnerships across the Balkans, Caucasus, and beyond.":
    "Говорете с екипа ни за капацитет, трасета и партньорства на едро на Балканите, в Кавказ и отвъд.",
  "Contact Sales": "Свържете се с продажбите",
  "The case study is available in English only.": "Казусът е наличен само на английски.",
};

export const DEVELOPERS_BG: Catalogue = {
  "Network API": "Network API",
  "Real-time network data, peering info, facility locations.": "Данни за мрежата, пиъринг и локации на съоръженията.",
  "Metrics API": "Metrics API",
  "Indicative round-trip time and route from Sofia to every PoP (route model, not live telemetry).":
    "Ориентировъчно RTT и трасе от София до всеки PoP (по модел на трасетата, не са измервания на живо).",
  "Quote API": "Quote API",
  "Programmatic service quotes and pricing.": "Програмни оферти и цени за услуги.",
  "Provisioning API": "Provisioning API",
  "Zero-touch service provisioning.": "Автоматично активиране на услуги.",
  "Network topology, IX presence, facilities": "Топология на мрежата, точки за обмен, съоръжения",
  "Indicative RTT and route from Sofia (not live)": "Ориентировъчно RTT и трасе от София (не на живо)",
  "Submit inquiry or quote request": "Изпращане на запитване или искане за оферта",
  "AI-powered network consultation": "Консултация за мрежата с помощта на AI",
  None: "Не",
  "60/min": "60/мин.",
  "5/min": "5/мин.",
  "10/min": "10/мин.",
  "API-FIRST CARRIER": "ОПЕРАТОР С ОТВОРЕНИ API",
  "Programmable network infrastructure.": "Програмируема мрежова инфраструктура.",
  "Build on top of Sofia Connect's carrier-grade platform.": "Надградете върху операторската платформа на Sofia Connect.",
  "Available · coming soon": "Налични · предстоящи",
  "Auth Required": "Нужна автентикация",
  "Data APIs are read-only": "API-тата за данни са само за четене",
  "Rate Limit": "Лимит на заявките",
  "Available Endpoints": "Налични крайни точки",
  "API Overview": "Преглед на API-тата",
  Available: "Налично",
  "Coming Soon": "Предстои",
  Interactive: "Интерактивно",
  "Live API Explorer": "Изпробвайте API-тата",
  "Fetch real data from our production APIs in your browser.": "Вземете реални данни от работещите ни API-та направо в браузъра.",
  Endpoint: "Крайна точка",
  "Fetching...": "Зареждане…",
  "Try it": "Изпробвай",
  "Network Error": "Мрежова грешка",
  "Network error": "Мрежова грешка",
  Copied: "Копирано",
  Copy: "Копирай",
  "→ Sending request...": "→ Изпращане на заявката…",
  "← Response will appear here": "← Тук ще се появи отговорът",
  "Select an endpoint and click \"Try it\"": "Изберете крайна точка и натиснете „Изпробвай“",
  Integration: "Интеграция",
  "Code Examples": "Примерен код",
  Documentation: "Документация",
  "API Reference": "Справочник за API",
  Method: "Метод",
  Auth: "Автентикация",
  Description: "Описание",
  Authentication: "Автентикация",
  "Open by Default": "Отворени по подразбиране",
  "Currently, all public APIs are open and require no authentication. Enterprise API keys with higher rate limits and webhook support are available on request.":
    "Засега всички публични API-та са отворени и не изискват автентикация. При поискване предоставяме корпоративни API ключове с по-високи лимити и поддръжка на webhooks.",
  "SDKs & Resources": "SDK и ресурси",
  "External Tools": "Външни инструменти",
  "Explore our network data through industry-standard platforms and tools.": "Разгледайте данните за мрежата ни в утвърдени платформи и инструменти.",
  "Peering info, IX presence, facilities": "Пиъринг, точки за обмен, съоръжения",
  "BGP data, routing history, visibility": "BGP данни, история на маршрутите, видимост",
  "Open source tools and scripts": "Инструменти и скриптове с отворен код",
  "Enterprise APIs": "Корпоративни API",
  "Need higher rate limits or webhook integration?": "Трябват ви по-високи лимити или webhooks?",
  "Contact our engineering team to discuss enterprise API keys, webhooks, custom integrations, and dedicated endpoints.":
    "Свържете се с инженерите ни за корпоративни API ключове, webhooks, интеграции по поръчка и отделни крайни точки.",
  "Contact Engineering Team": "Свържете се с инженерите",
  // Proper names (kept as is)
  ASN: "ASN",
  "PeeringDB — AS47872": "PeeringDB — AS47872",
  "RIPE Stat": "RIPE Stat",
  GitHub: "GitHub",
};

export const STATUS_BG: Catalogue = {
  "IP Transit": "IP Transit",
  "DWDM Wavelengths": "DWDM вълни",
  "Ethernet / L2VPN": "Ethernet / L2VPN",
  "Dark Fiber — Sofia metro": "Тъмно влакно — София",
  "NaaS Portal": "NaaS портал",
  "DNS Services": "DNS услуги",
  "≤52 min/mo": "≤52 мин./мес.",
  "≤5 min/mo": "≤5 мин./мес.",
  "≤43 min/mo": "≤43 мин./мес.",
  "15 min": "15 мин.",
  "30 min": "30 мин.",
  "2 hours": "2 часа",
  "4 hours": "4 часа",
  "8 hours": "8 часа",
  "24 hours": "24 часа",
};

export const LOOKING_GLASS_BG: Catalogue = {
  Bulgaria: "България",
  Germany: "Германия",
  "Türkiye": "Турция",
  Greece: "Гърция",
  "United Kingdom": "Великобритания",
  Georgia: "Грузия",
  "United Arab Emirates": "ОАЕ",
  Armenia: "Армения",
  "BGP Route": "BGP маршрут",
  Traceroute: "Traceroute",
  Ping: "Ping",
  "Look up BGP routes for a prefix": "Потърсете BGP маршрутите за префикс",
  "Trace the path to a destination": "Проследете пътя до дадена цел",
  "Test reachability and latency": "Проверете достъпност и закъснение",
  Tool: "Инструмент",
  "Source PoP": "Начален PoP",
  "IP Prefix or ASN": "IP префикс или ASN",
  "Host or IP Address": "Хост или IP адрес",
  "e.g.": "напр.",
  or: "или",
  "Running...": "Изпълнява се…",
  "Run Query": "Изпълни заявката",
  "Points of Presence": "точки на присъствие",
  "View Network Map": "Карта на мрежата",
};

/* /legal hub: interface only. The documents themselves are not translated
 * here (no machine translation of legal text). */
export const LEGAL_HUB_BG: Catalogue = {
  "Terms of Service": "Общи условия",
  "Service descriptions, payment terms, liability limitations, termination conditions, and governing law for all Sofia Connect services.":
    "Описание на услугите, условия за плащане, ограничения на отговорността, прекратяване и приложимо право за всички услуги на Sofia Connect.",
  General: "Общи",
  "Acceptable Use Policy": "Политика за допустима употреба",
  "Rules governing permitted and prohibited uses of Sofia Connect's network infrastructure, including prohibitions on abuse, spam, and illegal activity.":
    "Правила за разрешената и забранената употреба на мрежата на Sofia Connect, включително забрани за злоупотреби, спам и незаконна дейност.",
  Network: "Мрежа",
  "Privacy Policy": "Политика за поверителност",
  "GDPR-compliant disclosure on how Sofia Connect EAD collects, processes, stores, and protects personal data, and your rights as a data subject.":
    "Как Sofia Connect EAD събира, обработва, съхранява и защитава лични данни съгласно GDPR и какви са правата ви като субект на данни.",
  GDPR: "GDPR",
  "SLA Summary": "Обобщение на SLA",
  "Uptime commitments, credit calculation formulas, exclusions, incident reporting procedures, and response time targets for all service tiers.":
    "Гарантирана наличност, формули за компенсации, изключения, подаване на сигнали за инциденти и срокове за реакция за всички нива на услугите.",
  Operations: "Експлоатация",
  "Master Services Agreement (MSA)": "Рамков договор за услуги (MSA)",
  "Standard framework agreement covering services, fees, liability, term, IP, force majeure, and governing law for all Sofia Connect customer relationships.":
    "Стандартен рамков договор: услуги, такси, отговорност, срок, интелектуална собственост, непреодолима сила и приложимо право за всички клиенти на Sofia Connect.",
  "SLA Annex (Annex A)": "Приложение за SLA (Приложение A)",
  "Availability targets, latency commitments, severity matrix, credit multipliers, and maintenance windows by service tier. Forms part of the MSA.":
    "Цели за наличност, ангажименти за закъснение, матрица на сериозност, коефициенти за компенсации и прозорци за профилактика по нива на услугата. Част от MSA.",
  "Data Processing Addendum (Annex B)": "Споразумение за обработване на данни (Приложение B)",
  "GDPR Article 28 addendum with Standard Contractual Clauses, sub-processor list, technical and organisational measures, and 72-hour breach notification. EN + BG.":
    "Приложение по чл. 28 от GDPR със стандартни договорни клаузи, списък на подизпълнителите, технически и организационни мерки и уведомяване за нарушение до 72 часа. На английски и български.",
  "Service Order Form Template": "Образец на поръчка за услуга",
  "Fillable template for ordering IP Transit, DWDM Wavelengths, Ethernet / L2VPN, or Dark Fiber services under an executed MSA.":
    "Попълваем образец за поръчка на IP Transit, DWDM вълни, Ethernet / L2VPN или тъмно влакно по сключен MSA.",
  "11 pages": "11 стр.",
  "7 pages": "7 стр.",
  "12 pages": "12 стр.",
  "4 pages": "4 стр.",
  Legal: "Правна информация",
  "Legal Documents": "Правни документи",
  "Policies and agreements governing your relationship with Sofia Connect EAD. These documents apply to all services delivered under ASN\u00a047872.":
    "Политики и договори, които уреждат отношенията ви със Sofia Connect EAD. Те важат за всички услуги, предоставяни под ASN 47872.",
  "The documents are available in English.": "Документите са на английски език.",
  "Public policies": "Публични политики",
  "Contract Documents": "Договорни документи",
  "Master Agreement & Annexes": "Рамков договор и приложения",
  "Standard contract pack used for customer engagements. Download for review by your legal and procurement teams. Custom redlines accepted for enterprise contracts.":
    "Стандартният пакет договори, с който работим с клиенти. Изтеглете го за преглед от вашия правен отдел и отдел „Доставки“. При корпоративни договори приемаме редакции.",
  Download: "Изтегли",
  "These documents are templates. Final executed versions, including any negotiated amendments, take precedence over the published templates.":
    "Тези документи са образци. Окончателно подписаните версии, заедно с договорените промени, имат предимство пред публикуваните образци.",
  Contact: "Контакт",
  "Legal enquiries:": "Правни въпроси:",
  "Technical / NOC:": "Технически въпроси / NOC:",
  "Phone:": "Телефон:",
};

export const PORTAL_LOGIN_BG: Catalogue = {
  "Client Portal": "Клиентски портал",
  "Sign in": "Вход",
  "Access your network services, billing and support.": "Достъп до услугите, фактурите и поддръжката ви.",
  Password: "Парола",
  "Magic link": "Линк по имейл",
  "Email address": "Имейл адрес",
  "Forgot?": "Забравена?",
  "Send magic link": "Изпрати линк за вход",
  "Check your inbox — we sent you a sign-in link.": "Проверете пощата си — изпратихме ви линк за вход.",
  "Don't have an account?": "Нямате профил?",
  "Contact sales": "Свържете се с продажбите",
  "Or explore the portal in demo mode": "Или разгледайте портала в демо режим",
};

export const RESOURCES_BG: Catalogue = {
  "Named customer case studies": "Казуси с конкретни клиенти",
  "Real, named customer engagements published in full detail on our case-studies page — including Zayo Europe (east–west corridor) and TEAM Telecom Armenia (400G DWDM into the Caucasus).":
    "Реални проекти с конкретни клиенти, описани подробно на страницата с казуси, сред тях Zayo Europe (коридорът Изток–Запад) и TEAM Telecom Armenia (400G DWDM към Кавказ).",
  "View case studies": "Към казусите",
  "Peering Policy": "Политика за пиъринг",
  "Sofia Connect operates an open peering policy at public internet exchanges. Review our peering requirements and how to establish a session.":
    "Sofia Connect има отворена политика за пиъринг в публичните точки за обмен на трафик. Вижте изискванията ни и как се установява сесия.",
  "View Peering Policy": "Към политиката за пиъринг",
  "SLA Summary": "Обобщение на SLA",
  "Our Service Level Agreements define uptime guarantees, fault-response windows, and escalation paths for all product lines.":
    "Споразуменията ни за ниво на обслужване определят гарантираната наличност, сроковете за реакция при повреда и реда за ескалация за всички услуги.",
  "View SLA Summary": "Към обобщението на SLA",
  "Acceptable Use Policy": "Политика за допустима употреба",
  "The AUP outlines permitted and prohibited uses of Sofia Connect's network services and the procedures for reporting violations.":
    "Политиката описва разрешената и забранената употреба на мрежовите услуги на Sofia Connect и как се подава сигнал за нарушение.",
  "View AUP": "Към политиката",
  "Q1 2026: Network Expansion Update": "I тримесечие на 2026: разширение на мрежата",
  "Q1 2026": "I трим. 2026",
  "Sofia Connect expanded DWDM capacity to 400G on the Sofia–Frankfurt route and added new peering at DE-CIX Frankfurt.":
    "Sofia Connect увеличи DWDM капацитета по трасето София–Франкфурт до 400G и добави нов пиъринг в DE-CIX Франкфурт.",
  "In Q1 2026, Sofia Connect completed a significant expansion of its core DWDM infrastructure. Key milestones include:":
    "През първото тримесечие на 2026 Sofia Connect разшири значително основната си DWDM инфраструктура. Най-важното:",
  "DWDM capacity on the Sofia–Frankfurt route upgraded to 400G coherent optics":
    "DWDM капацитетът по трасето София–Франкфурт е увеличен до 400G с кохерентна оптика",
  "New peering established at DE-CIX Frankfurt, adding access to 1,000+ connected networks":
    "Нов пиъринг в DE-CIX Франкфурт с достъп до над 1000 свързани мрежи",
  "Route diversity improvements on the Sofia–Istanbul segment": "По-добро разнообразие на трасетата по участъка София–Истанбул",
  "NaaS Portal updated with real-time capacity ordering for IP Transit": "NaaS порталът вече позволява поръчка на капацитет за IP Transit в реално време",
  "2025 Year in Review": "2025 година в обобщение",
  "December 2025": "Декември 2025",
  "A summary of network achievements in 2025: reach to Dubai through a partner and 15 new peering partners.":
    "Какво постигнахме с мрежата през 2025: достигнахме Дубай чрез партньор и добавихме 15 нови партньори за пиъринг.",
  "2025 was a year of significant network expansion for Sofia Connect. Highlights include:":
    "2025 беше година на голямо разрастване на мрежата на Sofia Connect. Най-важното:",
  "Extended reach to Dubai and the Gulf region through a long-haul partner":
    "Разширихме обхвата до Дубай и региона на Персийския залив чрез партньор за дълги трасета",
  "Added 15 new peering partners across European and Caucasus internet exchanges":
    "Добавихме 15 нови партньори за пиъринг в европейски и кавказки точки за обмен на трафик",
  "Committed to energy-efficient operations across all owned infrastructure":
    "Поехме ангажимент за енергийно ефективна работа на цялата собствена инфраструктура",
  "Launched NaaS Portal v2.0 with API-driven service provisioning": "Пуснахме NaaS портал 2.0 с активиране на услуги през API",
};
